// Paginas.tsx
//
// As PÁGINAS da landing, e o que o Google e o WhatsApp mostram dela.
//
// Por que este componente existe: até 15/08/2026 o editor só sabia a home. O
// caminho era `'/'` fixo na section, `atualizarPagina()` estava escrita no
// backend e SEM UM ÚNICO CHAMADOR, e o estado `arquivada` estava no DDL e
// ninguém o atribuía. Ou seja: a ADR §13 promete "o que ele escrever manda" e o
// dono não tinha onde escrever.
//
// Mesma divisão de trabalho de `EditorDoSite` e `Unidades`: aqui NÃO se salva.
// Sem service, sem token — quem grava é a section.

import React, { useEffect, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import CampoDeImagem from "./CampoDeImagem";

export interface PaginaDaLista {
  paginaUuid: string;
  caminho: string;
  titulo: string;
  descricao: string | null;
  imagemOg: string | null;
  estado: string;
}

interface Props {
  paginas: PaginaDaLista[];
  /** A página ABERTA no editor, já com título/descrição derivados do cadastro. */
  pagina: PaginaDaLista | null;
  caminho: string;
  salvando: boolean;
  onAbrir: (caminho: string) => void;
  onCriar: (caminho: string) => void;
  onSalvarMetadados: (dados: {
    titulo: string;
    descricao: string;
    imagemOg: string;
  }) => void;
  onArquivar: (paginaUuid: string, arquivada: boolean) => void;
}

const Paginas: React.FC<Props> = ({
  paginas,
  pagina,
  caminho,
  salvando,
  onAbrir,
  onCriar,
  onSalvarMetadados,
  onArquivar,
}) => {
  const [novoCaminho, setNovoCaminho] = useState("");
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [imagemOg, setImagemOg] = useState("");

  // Os campos seguem a página ABERTA. `paginaUuid` na dependência, e não o
  // objeto: o objeto é recriado a cada resposta e derrubaria o que o dono está
  // digitando a cada releitura.
  useEffect(() => {
    setTitulo(pagina?.titulo ?? "");
    setDescricao(pagina?.descricao ?? "");
    setImagemOg(pagina?.imagemOg ?? "");
  }, [pagina?.paginaUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const aberta = paginas.find((item) => item.caminho === caminho) ?? null;

  return (
    <AlfredoStyles.Cartao>
      <AlfredoStyles.CartaoTitulo>Páginas</AlfredoStyles.CartaoTitulo>
      <AlfredoStyles.CartaoSubtitulo>
        A página inicial (<code>/</code>) é a que responde no seu endereço. As
        outras ficam em <code>seuendereco.alfredo.food/sobre</code> e afins — e
        só existem depois de você publicar.
      </AlfredoStyles.CartaoSubtitulo>

      <AlfredoStyles.Linha>
        <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 240)" }}>
          <span>Página aberta no editor</span>
          <AlfredoStyles.Seletor
            value={caminho}
            onChange={(e) => onAbrir(e.target.value)}
          >
            {/* A home entra sempre, mesmo antes de existir linha no banco: é ela
                que o endereço público serve hoje, montada do cadastro. */}
            {!paginas.some((item) => item.caminho === "/") && (
              <option value="/">/ (a página inicial)</option>
            )}
            {paginas.map((item) => (
              <option key={item.paginaUuid} value={item.caminho}>
                {item.caminho === "/" ? "/ (a página inicial)" : item.caminho}
                {item.estado === "arquivada" ? " — fora do ar" : ""}
              </option>
            ))}
          </AlfredoStyles.Seletor>
        </AlfredoStyles.Campo>

        <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 200)" }}>
          <span>Criar página</span>
          <AlfredoStyles.Entrada
            placeholder="/sobre"
            value={novoCaminho}
            onChange={(e) => setNovoCaminho(e.target.value)}
          />
          <AlfredoStyles.CampoAjuda>
            Minúsculas, sem acento e sem espaço. Começa com barra.
          </AlfredoStyles.CampoAjuda>
        </AlfredoStyles.Campo>

        <AlfredoStyles.Button
          type="button"
          disabled={salvando || !novoCaminho.trim()}
          onClick={() => {
            onCriar(novoCaminho.trim());
            setNovoCaminho("");
          }}
        >
          Criar
        </AlfredoStyles.Button>

        {aberta && aberta.caminho !== "/" && (
          <AlfredoStyles.Button
            type="button"
            color={aberta.estado === "arquivada" ? "#26792f" : "#8f5e07"}
            disabled={salvando}
            onClick={() =>
              onArquivar(aberta.paginaUuid, aberta.estado !== "arquivada")
            }
          >
            {aberta.estado === "arquivada" ? "Trazer de volta" : "Tirar do ar"}
          </AlfredoStyles.Button>
        )}
      </AlfredoStyles.Linha>

      <AlfredoStyles.CartaoTitulo
        style={{ fontSize: "var(--fs-lg)", marginTop: "var(--sp-20)" }}
      >
        O que aparece no Google e no WhatsApp
      </AlfredoStyles.CartaoTitulo>
      <AlfredoStyles.CartaoSubtitulo>
        Deixe em branco para o Alfredo montar a partir do cadastro da sua loja —
        assim, se você renomear a marca, o título acompanha em vez de ficar preso
        no nome de ontem. <strong>Estes três campos entram no ar assim que você
        salva</strong>: eles não esperam o Publicar, porque não são blocos da
        página.
      </AlfredoStyles.CartaoSubtitulo>

      <AlfredoStyles.Campo>
        <span>Título</span>
        <AlfredoStyles.Entrada
          maxLength={120}
          placeholder={pagina?.titulo ?? "O nome da sua marca"}
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
      </AlfredoStyles.Campo>

      <AlfredoStyles.Campo>
        <span>Descrição</span>
        <AlfredoStyles.AreaTexto
          maxLength={300}
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
        <AlfredoStyles.CampoAjuda>
          É a frase embaixo do link no resultado de busca. Até 300 caracteres.
        </AlfredoStyles.CampoAjuda>
      </AlfredoStyles.Campo>

      <CampoDeImagem
        rotulo="Imagem do link (Open Graph)"
        valor={imagemOg}
        aoMudar={setImagemOg}
        ajuda={
          <>
            A foto que aparece quando alguém manda o seu link no WhatsApp. Envie
            do computador ou cole o endereço de uma que já esteja na internet.
          </>
        }
      />

      <AlfredoStyles.Linha>
        <AlfredoStyles.Button
          type="button"
          disabled={salvando || !pagina?.paginaUuid}
          onClick={() => onSalvarMetadados({ titulo, descricao, imagemOg })}
        >
          {salvando ? "Salvando…" : "Salvar título e descrição"}
        </AlfredoStyles.Button>
      </AlfredoStyles.Linha>
    </AlfredoStyles.Cartao>
  );
};

export default Paginas;
